import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles, RefreshCw, CheckCircle2, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { api, errMsg } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { LocateMe } from "@/components/LocateMe";
import { useConfig, supportWa } from "@/lib/config";

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = ["from-sky-400 to-blue-600", "from-amber-400 to-orange-500", "from-emerald-400 to-teal-600", "from-rose-400 to-pink-600", "from-violet-400 to-indigo-600", "from-cyan-400 to-sky-600"];
const STEPS = ["dz.s_lot", "dz.s_view", "dz.s_style", "dz.s_rooms", "dz.s_addons", "dz.s_exterior", "dz.s_pool", "dz.s_contact"];

function Choice({ opts, value, onPick, multi, testid, lang }) {
  const on = (k) => (multi ? value.includes(k) : value === k);
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid={testid}>
      {opts.map((o, i) => (
        <button type="button" key={o.key} onClick={() => onPick(o.key)} data-testid={`${testid}-${o.key}`}
          className={`relative text-start rounded-2xl border-2 p-4 min-h-[88px] transition-colors ${on(o.key) ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300"}`}>
          <span className={`block h-2 w-10 rounded-full bg-gradient-to-r ${COLORS[i % COLORS.length]}`} />
          <span className="mt-3 block font-semibold text-slate-900 text-sm sm:text-base">{lang === "es" ? o.es : o.en}</span>
          {on(o.key) && <CheckCircle2 className="absolute top-3 end-3 h-5 w-5 text-blue-600" />}
        </button>
      ))}
    </div>
  );
}

function Counter({ label, value, set, min = 1, max = 12, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between" data-testid={testid}>
      <span className="font-semibold">{label}</span>
      <span className="flex items-center gap-3">
        <button type="button" onClick={() => set(Math.max(min, value - 1))} className="h-9 w-9 rounded-full border flex items-center justify-center" data-testid={`${testid}-minus`}><Minus className="h-4 w-4" /></button>
        <span className="w-6 text-center font-mono text-lg" data-testid={`${testid}-value`}>{value}</span>
        <button type="button" onClick={() => set(Math.min(max, value + 1))} className="h-9 w-9 rounded-full border flex items-center justify-center" data-testid={`${testid}-plus`}><Plus className="h-4 w-4" /></button>
      </span>
    </div>
  );
}

function Result({ res, t }) {
  const cfg = useConfig();
  const [status, setStatus] = useState("pending");
  const [left, setLeft] = useState(2);
  const timer = useRef(null);
  const started = useRef(Date.now());
  useEffect(() => {
    started.current = Date.now();
    const poll = async () => {
      const r = await api.get(`/home-designs/${res.id}`).catch(() => null);
      let s = r?.data?.status || "pending";
      if (s === "pending" && Date.now() - started.current > 200000) s = "failed";
      setStatus(s);
      if (r?.data) setLeft(r.data.retries_left);
      if (s === "pending") timer.current = setTimeout(poll, 4000);
    };
    poll();
    return () => clearTimeout(timer.current);
  }, [res.id, status === "pending"]); // eslint-disable-line react-hooks/exhaustive-deps
  const retry = async () => { try { await api.post(`/home-designs/${res.id}/retry`); setStatus("pending"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="max-w-4xl mx-auto px-4 py-14 text-center" data-testid="design-result">
      <p className="font-mono text-sm text-slate-500">{res.ref}</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">{t(status === "done" ? "dz.ready" : "dz.creating")}</h1>
      <div className="mt-8 rounded-3xl overflow-hidden border border-slate-200 bg-slate-100 aspect-video flex items-center justify-center">
        {status === "done" && <img src={`${API}/api/home-designs/${res.id}/image`} alt="AI concept render of your custom home" className="h-full w-full object-cover" data-testid="design-image" />}
        {status === "pending" && <div className="text-slate-500 flex flex-col items-center gap-3" data-testid="design-pending"><Loader2 className="h-10 w-10 animate-spin text-blue-600" />{t("dz.wait")}</div>}
        {status === "failed" && <div className="flex flex-col items-center gap-3" data-testid="design-failed"><p className="text-slate-600">{t("dz.failed")}</p>{left > 0 && <Button onClick={retry} className="rounded-full" data-testid="design-retry-btn"><RefreshCw className="h-4 w-4 me-2" />{t("dz.retry")}</Button>}</div>}
      </div>
      <p className="mt-4 text-xs text-slate-500">{t("dz.disclaimer")}</p>
      <p className="mt-6 text-slate-700">{t("dz.next")}</p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <a href={supportWa(cfg, t("dz.wa", { ref: res.ref }))} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full bg-[#25D366] text-white font-semibold px-6 h-11" data-testid="design-wa-btn">WhatsApp</a>
        <Link to="/build" className="inline-flex items-center rounded-full border px-6 h-11" data-testid="design-back-btn">{t("b.nav")}</Link>
      </div>
    </div>
  );
}

export default function DesignHome() {
  const { t, lang } = useI18n();
  useSeo(t("dz.title"), t("dz.sub"));
  const [opts, setOpts] = useState(null);
  const [builders, setBuilders] = useState([]);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [f, setF] = useState({ lot: "", view: "", style: "", bedrooms: 3, bathrooms: 2, floors: 1, addons: [], exterior: "", roof: "", pool: "", garage: "", notes: "", name: "", phone: "", email: "", city: "", builder: "any", geo: null, website: "" });
  useEffect(() => { api.get("/home-designs/options").then((r) => setOpts(r.data)); api.get("/builders").then((r) => setBuilders(r.data)); }, []);
  const pick = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (v) => setF((p) => ({ ...p, addons: p.addons.includes(v) ? p.addons.filter((x) => x !== v) : [...p.addons, v] }));
  const valid = [f.lot, f.view, f.style, true, true, f.exterior && f.roof, f.pool && f.garage, f.name.trim() && f.phone.trim()][step];
  const next = () => { if (!valid) { toast.error(t("req.required")); return; } setStep(step + 1); window.scrollTo(0, 0); };
  const submit = async () => {
    if (!valid) { toast.error(t("req.required")); return; }
    setBusy(true);
    try { const r = await api.post("/home-designs", f); setRes(r.data); window.scrollTo(0, 0); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (res) return <Result res={res} t={t} />;
  if (!opts) return <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const body = [
    <Choice opts={opts.lot} value={f.lot} onPick={pick("lot")} testid="dz-lot" lang={lang} />,
    <Choice opts={opts.view} value={f.view} onPick={pick("view")} testid="dz-view" lang={lang} />,
    <Choice opts={opts.style} value={f.style} onPick={pick("style")} testid="dz-style" lang={lang} />,
    <div className="space-y-3">
      <Counter label={t("cl.beds")} value={f.bedrooms} set={pick("bedrooms")} testid="dz-beds" />
      <Counter label={t("cl.baths")} value={f.bathrooms} set={pick("bathrooms")} testid="dz-baths" />
      <Counter label={t("dz.floors")} value={f.floors} set={pick("floors")} max={4} testid="dz-floors" />
    </div>,
    <Choice opts={opts.addons} value={f.addons} onPick={toggle} multi testid="dz-addons" lang={lang} />,
    <div className="space-y-6"><p className="font-semibold">{t("dz.walls")}</p><Choice opts={opts.exterior} value={f.exterior} onPick={pick("exterior")} testid="dz-exterior" lang={lang} />
      <p className="font-semibold">{t("dz.roof")}</p><Choice opts={opts.roof} value={f.roof} onPick={pick("roof")} testid="dz-roof" lang={lang} /></div>,
    <div className="space-y-6"><p className="font-semibold">{t("dz.pool")}</p><Choice opts={opts.pool} value={f.pool} onPick={pick("pool")} testid="dz-pool" lang={lang} />
      <p className="font-semibold">{t("dz.garage")}</p><Choice opts={opts.garage} value={f.garage} onPick={pick("garage")} testid="dz-garage" lang={lang} /></div>,
    <div className="grid sm:grid-cols-2 gap-4">
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" value={f.website} onChange={set("website")} />
      <div className="sm:col-span-2 space-y-1.5"><Label>{t("dz.notes")}</Label><Textarea rows={3} value={f.notes} onChange={set("notes")} data-testid="dz-notes-input" /></div>
      <div className="space-y-1.5"><Label>{t("f.name")} *</Label><Input value={f.name} onChange={set("name")} data-testid="dz-name-input" /></div>
      <div className="space-y-1.5"><Label>{t("f.phone")} *</Label><Input value={f.phone} onChange={set("phone")} data-testid="dz-phone-input" /></div>
      <div className="space-y-1.5"><Label>{t("f.email")}</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="dz-email-input" /></div>
      <div className="space-y-1.5"><Label>{t("f.city")}</Label><Input value={f.city} onChange={set("city")} data-testid="dz-city-input" /></div>
      <div className="sm:col-span-2 space-y-1.5"><Label>{t("b.builder")}</Label>
        <div className="flex flex-wrap gap-2">{[...builders.map((b) => [b.key, b.name]), ["any", t("b.any")]].map(([k, l]) => (
          <button type="button" key={k} onClick={() => setF({ ...f, builder: k })} data-testid={`dz-builder-${k}`} className={`px-4 py-2 rounded-full border text-sm ${f.builder === k ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-300"}`}>{l}</button>
        ))}</div>
      </div>
      <LocateMe geo={f.geo} onFound={({ geo, city }) => setF((p) => ({ ...p, geo, city: p.city || city }))} />
    </div>,
  ];
  return (
    <div className="max-w-4xl mx-auto px-4 pt-12 pb-28" data-testid="design-page">
      <p className="text-xs font-semibold uppercase tracking-widest text-blue-700 flex items-center gap-2"><Sparkles className="h-4 w-4" />{t("dz.kicker")}</p>
      <h1 className={`mt-2 font-extrabold tracking-tight ${step === 0 ? "text-4xl sm:text-5xl" : "text-2xl sm:text-3xl"}`}>{t("dz.title")}</h1>
      {step === 0 && <p className="mt-3 text-slate-600">{t("dz.sub")}</p>}
      <div className="mt-6 flex justify-between text-sm"><span className="font-semibold text-blue-700" data-testid="dz-step-label">{t(STEPS[step])}</span><span className="font-mono text-slate-500">{step + 1} / {STEPS.length}</span></div>
      <Progress value={((step + 1) / STEPS.length) * 100} className="mt-2 h-2" />
      <div className="mt-8 fade-up" key={step}>{body[step]}</div>
      <div className="mt-8 mb-16 flex justify-between">
        <Button variant="outline" className="rounded-full" disabled={step === 0} onClick={() => { setStep(step - 1); window.scrollTo(0, 0); }} data-testid="dz-back-btn">{t("req.back")}</Button>
        {step < STEPS.length - 1
          ? <Button className="rounded-full bg-blue-600 hover:bg-blue-700 px-8" onClick={next} data-testid="dz-next-btn">{t("req.next")}</Button>
          : <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-8" disabled={busy} onClick={submit} data-testid="dz-submit-btn">{busy ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Sparkles className="h-4 w-4 me-2" />}{t("dz.submit")}</Button>}
      </div>
    </div>
  );
}
